/**
 * JoinNameContainer — Name screen for the group invite flow
 * Route: /groups/join/:gid/name?inviter=<uid>
 *
 * Shown after phone verification ONLY when the signed-in user's /users/{uid}
 * doc has no name. Without this step, web joiners appear to every other group
 * member as "Member" forever. Writes the SAME `name` field the mobile app
 * writes (saveName), so the backend onProfileUpdated trigger heals any frozen
 * member snippets.
 *
 * Guarantees:
 * - Users with a saved name never see the form: JoinVerifyContainer checks the
 *   doc before routing here, and this screen re-checks behind a spinner (for
 *   refreshes / direct URL landings) and forwards named users to the handoff.
 * - Never traps the user: if the name write fails, log it and continue to the
 *   handoff anyway. The app can collect a name later.
 */
import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  brand,
  fontSans,
  spacing,
  radii,
  shadows,
} from '../../style/splytBrand';
import useJoinPageChrome from './useJoinPageChrome';
import {
  subscribeToLoginChanges,
  getProfileName,
  saveProfileName,
} from '../../API/users';
import { track, EVENTS, SURFACE, ONBOARDING_STEP } from '../../analytics/track';
import { normalizeJoinName, withTimeout, readInviteParams, buildInviteQuery } from './inviteHelpers';
import JoinFooter from './JoinFooter';
import useKeyboardPinnedFooter from './useKeyboardPinnedFooter';

const NAME_MAX_LENGTH = 40;

// ─── Subcomponents ────────────────────────────────────────────────────────────

// Same wordmark the other join screens draw, fixed at the header size this
// screen uses (prop-less so react/prop-types stays quiet).
function Wordmark() {
  const size = 22;
  const color = brand.caviar;
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        fontFamily: fontSans,
        fontWeight: 700,
        fontSize: size,
        letterSpacing: '-0.04em',
        color,
      }}
    >
      <svg width={size * 0.95} height={size * 0.95} viewBox="0 0 24 24" fill="none">
        <path
          d="M6 4h8a4 4 0 010 8H10a4 4 0 010 8h-4"
          stroke={color}
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      splyt
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    // svh, matching the wrapper below. `100vh` is the LARGE viewport on iOS
    // Safari (toolbars hidden), so it made the document ~80 to 100px taller
    // than the visible area and the page scrolled for no reason.
    minHeight: '100vh',
    '@supports (height: 100svh)': {
      minHeight: '100svh',
    },
    backgroundColor: brand.marsh,
    fontFamily: fontSans,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },

  // Mobile layout
  mobileWrapper: {
    display: 'flex',
    flexDirection: 'column',
    // border-box: padding must not be added outside the viewport-sized column.
    boxSizing: 'border-box',
    minHeight: '100vh',
    // svh, not dvh: iOS Chrome treats dvh as the bars-hidden height and lays
    // the footer out behind the bottom toolbar (seen live on the handoff page).
    '@supports (height: 100svh)': {
      minHeight: '100svh',
    },
    padding: `${spacing.md}px ${spacing.md + 4}px 0`,
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },

  // Desktop layout
  desktopWrapper: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '24px 40px 40px',
    },
  },

  desktopHeader: {
    width: '100%',
    maxWidth: 1080,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 28,
  },

  desktopCard: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: brand.surface,
    borderRadius: radii.card,
    padding: 36,
    boxShadow: shadows.card,
    border: `1px solid ${brand.marshNeutral}`,
  },

  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    paddingBottom: spacing.md,
  },

  desktopContent: {
    display: 'flex',
    flexDirection: 'column',
  },

  // Title
  title: {
    margin: 0,
    fontWeight: 700,
    letterSpacing: '-0.025em',
    color: brand.caviar,
  },

  titleMobile: {
    fontSize: 26,
    lineHeight: 1.1,
  },

  titleDesktop: {
    fontSize: 30,
    lineHeight: 1.1,
  },

  subtitle: {
    margin: '6px 0 22px',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1.45,
    color: brand.caviar75,
  },

  // Name input (same treatment as the phone input on JoinPhoneContainer)
  nameInputWrapper: {
    backgroundColor: brand.surface,
    borderRadius: radii.input,
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
  },

  nameInputWrapperDefault: {
    border: `1.5px solid ${brand.marshNeutral}`,
  },

  nameInputWrapperFocused: {
    border: `1.5px solid ${brand.caviar}`,
    boxShadow: shadows.inputFocus,
  },

  nameInput: {
    flex: 1,
    // Parity with the phone input: an <input>'s intrinsic width must never set
    // the row's floor (see JoinPhoneContainer's phoneInputWrapper).
    minWidth: 0,
    width: '100%',
    border: 'none',
    outline: 'none',
    fontFamily: fontSans,
    fontWeight: 500,
    fontSize: 17,
    lineHeight: 1,
    color: brand.caviar,
    backgroundColor: 'transparent',
    '&::placeholder': {
      color: brand.caviar50,
    },
  },

  // Footer
  footer: {
    flexShrink: 0,
    padding: `${spacing.sm}px 0`,
    paddingBottom: `calc(${spacing.lg}px + env(safe-area-inset-bottom, 0px))`,
  },

  desktopFooter: {
    marginTop: spacing.lg,
  },

  // Primary button
  primaryButton: {
    width: '100%',
    backgroundColor: brand.caviar,
    color: brand.surface,
    border: 'none',
    borderRadius: radii.pill,
    padding: '16px 22px',
    fontFamily: fontSans,
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 1.1,
    letterSpacing: '-0.005em',
    boxShadow: shadows.primaryButton,
    cursor: 'pointer',
    transition: 'transform 120ms ease, opacity 150ms ease',
    '&:hover:not(:disabled)': {
      transform: 'scale(0.985)',
    },
    '&:active:not(:disabled)': {
      transform: 'scale(0.98)',
    },
    '&:disabled': {
      backgroundColor: brand.caviar25,
      boxShadow: 'none',
      cursor: 'not-allowed',
    },
  },

  // Header link
  headerLink: {
    color: brand.blueberry,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.3,
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    borderBottom: `1.5px solid ${brand.blueberry25}`,
    padding: 0,
    paddingBottom: 1,
    cursor: 'pointer',
    fontFamily: fontSans,
  },

  // Checking state (same treatment as the landing's loading state)
  stateWrap: {
    minHeight: '100vh',
    '@supports (height: 100svh)': { minHeight: '100svh' },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 32px',
  },

  spinner: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: `3px solid ${brand.marshNeutral}`,
    borderTopColor: brand.caviar,
    animation: '$spin 800ms linear infinite',
  },

  '@keyframes spin': {
    to: { transform: 'rotate(360deg)' },
  },
}));

// ─── Main Component ───────────────────────────────────────────────────────────

export default function JoinNameContainer() {
  useJoinPageChrome();
  const classes = useStyles();
  const { gid } = useParams();
  const history = useHistory();
  const location = useLocation();

  const { inviter, inviteCode, utmSource } = readInviteParams(location.search);
  const query = buildInviteQuery({ inviter, inviteCode, utmSource });

  // null = still checking auth + the user doc (spinner). Set only when the
  // signed-in user has NO saved name, which is the only case that shows the form.
  const [uid, setUid] = useState(null);
  const [name, setName] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const inputRef = useRef(null);
  // Lifts Continue above the software keyboard on iOS (see the hook's header).
  const { footerRef, footerHeight, pinned } = useKeyboardPinnedFooter();

  // Gate: wait for auth to settle, load the user doc, and only reveal the form
  // when there is no saved name. Everyone else is forwarded to the handoff so
  // named users never see this screen.
  useEffect(() => {
    let active = true;
    let settled = false;

    // If auth never reports within 6s (stalled restore), stop spinning and
    // fail open to the handoff. Nobody waits here.
    const authTimer = setTimeout(() => {
      if (settled || !active) return;
      settled = true;
      history.replace(`/groups/join/${gid}/app${query}`);
    }, 6000);

    const unsubscribe = subscribeToLoginChanges(async (user) => {
      if (settled || !active) return;
      settled = true;

      // No signed-in user (direct URL landing): nothing we could write anyway.
      if (!user || !user.uid) {
        history.replace(`/groups/join/${gid}/app${query}`);
        return;
      }

      try {
        // 4s budget: a stalled read must not hold this spinner. Timing out
        // shows the form; only users verify already routed here land on it,
        // and typing a name is always safe.
        const existingName = await withTimeout(getProfileName(user.uid), 4000, '');
        if (!active) return;
        if (existingName) {
          history.replace(`/groups/join/${gid}/app${query}`);
        } else {
          setUid(user.uid);
        }
      } catch (err) {
        // Can't tell whether a name exists: fail open toward the original flow.
        console.error('[JoinName] name check failed:', err);
        if (active) history.replace(`/groups/join/${gid}/app${query}`);
      }
    });

    return () => {
      active = false;
      clearTimeout(authTimer);
      unsubscribe();
    };
  }, [history, gid, query]);

  // Focus the input once the form is revealed (same pattern as the verify screen)
  useEffect(() => {
    if (uid) inputRef.current?.focus();
  }, [uid]);

  const cleanedName = normalizeJoinName(name);
  const isValidName = cleanedName !== '';

  const handleNameChange = (e) => {
    setName(e.target.value.slice(0, NAME_MAX_LENGTH));
  };

  const handleSubmit = async () => {
    if (!isValidName || isSaving || !uid) return;

    setIsSaving(true);
    try {
      // 4s budget: a stalled write must not hold "Saving...". The write stays
      // queued client-side and still lands; the person moves on either way.
      await withTimeout(saveProfileName(uid, cleanedName), 4000, undefined);
      track(EVENTS.ONBOARDING_STEP_COMPLETED, { step: ONBOARDING_STEP.NAME, surface: SURFACE.GROUP });
    } catch (err) {
      // A failed write must never trap the user here. Continue to the handoff;
      // the app can collect a name later.
      console.error('[JoinName] name write failed:', err);
    }
    history.push(`/groups/join/${gid}/app${query}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  // ─── Checking (auth + user doc) ───────────────────────────────────────────
  if (!uid) {
    return (
      <div className={classes.root}>
        <div className={classes.stateWrap}>
          <div className={classes.spinner} />
        </div>
      </div>
    );
  }

  // Shared content renderer
  const renderContent = (isMobile) => (
    <>
      <h2
        className={`${classes.title} ${
          isMobile ? classes.titleMobile : classes.titleDesktop
        }`}
      >
        What should your group call you?
      </h2>

      <p className={classes.subtitle}>
        Your name shows next to what you owe and what you paid.
      </p>

      {/* Name input */}
      <div
        className={`${classes.nameInputWrapper} ${
          isFocused
            ? classes.nameInputWrapperFocused
            : classes.nameInputWrapperDefault
        }`}
      >
        {/* enterkeyhint is lowercase on purpose: React 16 passes unknown
            lowercase attributes straight through but warns on camelCase ones. */}
        <input
          ref={inputRef}
          type="text"
          autoComplete="name"
          // eslint-disable-next-line react/no-unknown-property
          enterkeyhint="done"
          maxLength={NAME_MAX_LENGTH}
          className={classes.nameInput}
          placeholder="Your name"
          value={name}
          onChange={handleNameChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={isSaving}
          aria-label="Your name"
        />
      </div>
    </>
  );

  // Footer content
  const renderFooter = () => (
    <button
      type="button"
      className={classes.primaryButton}
      disabled={!isValidName || isSaving}
      onClick={handleSubmit}
      // iOS fires blur before click: without this the first tap on the pinned
      // bar only closes the keyboard and the button slides out from under the finger.
      onMouseDown={(e) => e.preventDefault()}
    >
      {isSaving ? 'Saving...' : 'Continue'}
    </button>
  );

  return (
    <div className={classes.root}>
      {/* Mobile layout */}
      <div className={classes.mobileWrapper}>
        <div className={classes.content} style={{ paddingBottom: footerHeight }}>
          {renderContent(true)}
        </div>
        <JoinFooter footerRef={footerRef} pinned={pinned}>
          {renderFooter()}
        </JoinFooter>
      </div>

      {/* Desktop layout */}
      <div className={classes.desktopWrapper}>
        <div className={classes.desktopHeader}>
          <Wordmark />
          <button type="button" className={classes.headerLink}>
            Already have Splyt?
          </button>
        </div>
        <div className={classes.desktopCard}>
          <div className={classes.desktopContent}>{renderContent(false)}</div>
          <div className={classes.desktopFooter}>{renderFooter()}</div>
        </div>
      </div>
    </div>
  );
}
